import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleDot, FileEdit, Newspaper, Pencil, Plus, Search } from 'lucide-react';
import { AuthContext } from '../../../contexts/AuthContext';
import { listenAllNews, NEWS_STATUS } from '../../../database/news';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import PageHeader from '../shell/PageHeader';

const fmt = (ts) => {
  if (!ts) return '—';
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

const Row = ({ post }) => (
  <li className="grid grid-cols-[1fr_auto_auto] items-center gap-6 border-b border-border/60 px-2 py-4 last:border-b-0">
    <div className="min-w-0">
      <Link
        to={`/admin/news/${post.id}`}
        className="truncate font-display text-base font-medium text-foreground hover:text-heat"
      >
        {post.title || 'Untitled post'}
      </Link>
      <div className="mt-0.5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {post.slug ? <span>/news/{post.slug}</span> : <span>no slug</span>}
        <span>·</span>
        <span>
          {post.status === NEWS_STATUS.PUBLISHED ? (
            <span className="inline-flex items-center gap-1 text-heat">
              <CircleDot className="h-2.5 w-2.5" />
              Published {fmt(post.publishedAt)}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <FileEdit className="h-2.5 w-2.5" />
              Draft · updated {fmt(post.updatedAt)}
            </span>
          )}
        </span>
      </div>
    </div>
    <div className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground sm:block">
      {(post.blocks || []).length} block{(post.blocks || []).length === 1 ? '' : 's'}
    </div>
    <Button variant="ghost" size="sm" asChild>
      <Link to={`/admin/news/${post.id}`}>
        <Pencil className="h-3.5 w-3.5" />
        Edit
      </Link>
    </Button>
  </li>
);

const NewsList = () => {
  const { currentUser, hasPermission } = useContext(AuthContext);
  const canManage = hasPermission('news.manage');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');

  useEffect(() => {
    if (!currentUser || !canManage) return undefined;
    const unsub = listenAllNews((data) => {
      setItems(data);
      setLoading(false);
    });
    return unsub;
  }, [currentUser, canManage]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((p) => {
      if (tab === 'drafts' && p.status !== NEWS_STATUS.DRAFT) return false;
      if (tab === 'published' && p.status !== NEWS_STATUS.PUBLISHED) return false;
      if (term) {
        const hay = `${p.title || ''} ${p.slug || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [items, tab, search]);

  const counts = useMemo(
    () => ({
      all: items.length,
      drafts: items.filter((p) => p.status === NEWS_STATUS.DRAFT).length,
      published: items.filter((p) => p.status === NEWS_STATUS.PUBLISHED).length,
    }),
    [items]
  );

  if (!canManage) {
    return (
      <div className="py-20 text-center text-sm text-muted-foreground">
        You need content admin access to manage news.
      </div>
    );
  }

  return (
    <div className="pb-16">
      <PageHeader
        eyebrow="Content"
        title="News"
        description="Write, preview, and publish announcements. Drafts stay hidden until you hit publish."
        actions={
          <Button asChild>
            <Link to="/admin/news/new">
              <Plus className="h-4 w-4" />
              New post
            </Link>
          </Button>
        }
      />

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title or slug…"
            className="pl-9"
          />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all">All · {counts.all}</TabsTrigger>
          <TabsTrigger value="drafts">Drafts · {counts.drafts}</TabsTrigger>
          <TabsTrigger value="published">Published · {counts.published}</TabsTrigger>
        </TabsList>
        <TabsContent value={tab}>
          {loading ? (
            <div className="border-y border-border/60">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex h-16 animate-pulse items-center gap-6 px-2">
                  <div className="h-4 w-60 bg-muted" />
                  <div className="ml-auto h-3 w-24 bg-muted" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="border border-dashed border-border/60 px-6 py-16 text-center text-sm text-muted-foreground">
              <Newspaper className="mx-auto mb-3 h-8 w-8 opacity-50" />
              {search ? 'No posts match your search.' : 'No posts yet — create your first one.'}
            </div>
          ) : (
            <ul className="border-y border-border/60">
              {filtered.map((p) => (
                <Row key={p.id} post={p} />
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NewsList;
